import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import DashboardLayout from "./components/DashboardLayout";
import { SplashScreen } from "./components/SplashScreen";
import Home from "./pages/Home";
import SMSParser from "./pages/SMSParser";
import Transactions from "./pages/Transactions";
import IncomeStatement from "./pages/IncomeStatement";
import Analytics from "./pages/Analytics";
import Budgets from "./pages/Budgets";
import ExportPage from "./pages/ExportPage";
import AccountSettings from "./pages/AccountSettings";
import AccountsManagement from "./pages/AccountsManagement";
import Setup from "./pages/Setup";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import MarketingLanding from "./pages/MarketingLanding";
import SignIn from "./pages/SignIn";
import AccountType from "./pages/AccountType";
import AdminBusinessWaitlist from "./pages/AdminBusinessWaitlist";
import EntityManagement from "./pages/EntityManagement";
import Documents from "./pages/Documents";
import Counterparties from "./pages/Counterparties";
import Invoices from "./pages/Invoices";
import Reconciliation from "./pages/Reconciliation";
import FxRateManagement from "./pages/FxRateManagement";
import Ledger from "./pages/Ledger";
import BusinessStatements from "./pages/BusinessStatements";
import Contracts from "./pages/Contracts";
import BusinessAnalytics from "./pages/BusinessAnalytics";
import Services from "./pages/Services";
import BusinessUpload from "./pages/BusinessUpload";
import QaydHowToFile from "./pages/QaydHowToFile";
import { useState, useCallback, useEffect } from "react";
import { GlobalQueryErrorNotice, GlobalQueryErrorNoticeBoundary } from "./components/GlobalQueryErrorNotice";

function Router() {
  const LegacyRedirect = ({ to }: { to: string }) => {
    useEffect(() => { window.location.replace(to); }, [to]);
    return null;
  };
  return (
    <Switch>
      <Route path="/" component={MarketingLanding} />
      <Route path="/signin" component={SignIn} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/account-type" component={AccountType} />
      <Route path="/setup">{() => { window.location.replace("/account"); return null; }}</Route>
      <Route>
        <DashboardLayout>
          <Switch>
            <Route path="/dashboard" component={Home} />
            <Route path="/sms" component={SMSParser} />
            <Route path="/transactions" component={Transactions} />
            <Route path="/income-statement" component={IncomeStatement} />
            <Route path="/analytics" component={Analytics} />
            <Route path="/budgets" component={Budgets} />
            <Route path="/export" component={ExportPage} />
            <Route path="/accounts" component={AccountsManagement} />
            <Route path="/account" component={AccountSettings} />
            <Route path="/entities">{() => <LegacyRedirect to="/account?tab=entities" />}</Route>
            <Route path="/documents" component={Documents} />
            <Route path="/counterparties" component={Counterparties} />
            <Route path="/invoices" component={Invoices} />
            <Route path="/business-upload" component={BusinessUpload} />
            <Route path="/statement-import" component={Ledger} />
            <Route path="/business-analytics" component={BusinessAnalytics} />
            <Route path="/services" component={Services} />
            <Route path="/reconciliation">{() => <LegacyRedirect to="/invoices?tab=matching" />}</Route>
            <Route path="/ledger">{() => <LegacyRedirect to="/business-statements?tab=trial" />}</Route>
            <Route path="/business-statements" component={BusinessStatements} />
            <Route path="/qayd/how-to-file" component={QaydHowToFile} />
            <Route path="/contracts">{() => <LegacyRedirect to="/documents?type=contract" />}</Route>
            <Route path="/admin/business-waitlist" component={AdminBusinessWaitlist} />
            <Route path="/admin/fx-rates">{() => <LegacyRedirect to="/account?tab=fx" />}</Route>
            <Route path="/404" component={NotFound} />
            <Route component={NotFound} />
          </Switch>
        </DashboardLayout>
      </Route>
    </Switch>
  );
}

function SetupWrapper() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#1a1d2b" }}>
      <Setup />
    </div>
  );
}

function App() {
  const [location] = useLocation();
  const [showSplash, setShowSplash] = useState(() => {
    // Only show splash on initial app load (PWA launch), not on navigation
    const hasShown = sessionStorage.getItem("splash-shown");
    return !hasShown;
  });

  const handleSplashFinished = useCallback(() => {
    setShowSplash(false);
    sessionStorage.setItem("splash-shown", "1");
  }, []);

  const isPublicRoute = ["/", "/signin", "/privacy", "/terms", "/account-type"].includes(location);
  const shouldRenderSplash = showSplash && !isPublicRoute;

  // Remove native splash if React splash is not shown
  useEffect(() => {
    if (!shouldRenderSplash) {
      const nativeSplash = document.getElementById("native-splash");
      if (nativeSplash) {
        nativeSplash.style.opacity = "0";
        setTimeout(() => nativeSplash.remove(), 300);
      }
    }
  }, [shouldRenderSplash]);

  return (
    <ErrorBoundary>
      <ThemeProvider
        defaultTheme="dark"
        switchable
      >
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <GlobalQueryErrorNoticeBoundary>
              <GlobalQueryErrorNotice />
            </GlobalQueryErrorNoticeBoundary>
            {shouldRenderSplash && <SplashScreen onFinished={handleSplashFinished} />}
            <Router />
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
